import { useState, useRef, useCallback } from 'react';
import { useFormContext, FieldPath, useWatch } from 'react-hook-form';
import axios from 'axios';
import { OrderSchemaType, ItemOrderSchemaType, ItemPath } from '../order.schema';

type AttachmentFieldPath = Extract<FieldPath<OrderSchemaType>, 'attachments'> | `${ItemPath}.${string}.anexos`;

interface AttachmentHandlerProps {
  fieldPath: AttachmentFieldPath;
}

const MAX_TOTAL_SIZE = 5 * 1024 * 1024;

export const useAttachmentHandler = ({ fieldPath }: AttachmentHandlerProps) => {
  const { control, setValue, getValues } = useFormContext<OrderSchemaType>();
  const attachmentNames: string[] = useWatch({ control, name: fieldPath, defaultValue: [] }) || [];

  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelectClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveAttachment = useCallback((indexToRemove: number) => {
    const currentFileNames: string[] = getValues(fieldPath) || [];
    const newFileNames = currentFileNames.filter((_, index) => index !== indexToRemove);

    if (fieldPath === 'attachments') {
       const currentFileIds: number[] = getValues('fileIds') || [];
       const newFileIds = currentFileIds.filter((_, index) => index !== indexToRemove);
       setValue('fileIds', newFileIds, { shouldValidate: true });
    }

    const newSelectedFiles = selectedFiles.filter((_, index) => index !== indexToRemove);
    setSelectedFiles(newSelectedFiles);

    setValue(fieldPath, newFileNames, { shouldValidate: true, shouldDirty: true });

    const totalSize = newSelectedFiles.reduce((acc, file) => acc + file.size, 0);
    if (totalSize <= MAX_TOTAL_SIZE) {
      setAttachmentError(null);
    }
  }, [getValues, setValue, fieldPath, selectedFiles]);

  const handleRemoveAllAttachments = useCallback(() => {
    setValue(fieldPath, [], { shouldValidate: true });
    if (fieldPath === 'attachments') {
       setValue('fileIds', [], { shouldValidate: true });
    }
    setSelectedFiles([]);
    setAttachmentError(null);
  }, [fieldPath, setValue]);



  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentFileNames: string[] = getValues(fieldPath) || [];
    const currentFileIds: number[] = fieldPath === 'attachments' ? (getValues('fileIds') || []) : [];
    
    const newSelectedFiles = [...selectedFiles, ...files];

    const totalSize = newSelectedFiles.reduce((acc, file) => acc + file.size, 0);

    if (totalSize > MAX_TOTAL_SIZE) {
      setAttachmentError('O tamanho total dos anexos não pode exceder 5MB.');
      return;
    }

    try {
      setAttachmentError('Fazendo upload...');
      const uploadedFilesInfo = await Promise.all(files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);
        const UPLOAD_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/order/upload-attachment`;
        const { data } = await axios.post<{ id: number; fileUrl: string }>(UPLOAD_URL, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return { id: data.id, fileUrl: data.fileUrl, name: file.name };
      }));
      
      if (fieldPath === 'attachments') {
        const uploadedIds = uploadedFilesInfo.map(info => info.id);
        setValue('fileIds', [...currentFileIds, ...uploadedIds], { shouldValidate: true });
        const newFileNames = [...currentFileNames, ...uploadedFilesInfo.map(info => info.name)];
        setValue(fieldPath, newFileNames, { shouldValidate: true });
      } else {
        const uploadedUrls = uploadedFilesInfo.map(info => info.fileUrl);
        const newFileNames = [...currentFileNames, ...uploadedUrls];
        setValue(fieldPath, newFileNames, { shouldValidate: true });
      }

      setAttachmentError(null);
    } catch (err) {
      console.error('Erro ao fazer upload do anexo:', err);
      setAttachmentError('Erro ao fazer upload do anexo.');
      return;
    }

    setSelectedFiles(newSelectedFiles);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [getValues, fieldPath, setValue, selectedFiles]);

  return {
    attachments: selectedFiles,
    attachmentNames,
    attachmentError,
    fileInputRef,
    handleFileSelectClick,
    handleFileChange,
    handleRemoveAttachment,
    handleRemoveAllAttachments,
  };
};